import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SoftLaunch.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRocket, 
  faCheckCircle, 
  faPlay,
  faClock,
  faUsers,
  faTag,
  faCross,
  faCalculator,
  faArrowRight,
  faExclamationTriangle,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import { addToWaitlist, getWaitlistCount } from '../../services/waitlistService';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const SoftLaunch: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(4247);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [error, setError] = useState<string | null>(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  // Target date: 4 weeks from today (28 days)
  const launchDate = new Date(Date.now() + (28 * 24 * 60 * 60 * 1000));

  // Load initial subscriber count
  useEffect(() => {
    const loadSubscriberCount = async () => {
      try {
        const count = await getWaitlistCount();
        setSubscriberCount(count);
      } catch (error) {
        console.error('Failed to load subscriber count:', error);
      }
    };
    
    loadSubscriberCount();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setIsDetectingLocation(true);
    setError(null);
    
    try {
      const success = await addToWaitlist(email);
      
      if (success) {
        setIsSubscribed(true);
        setSubscriberCount(prev => prev + 1);
      } else {
        throw new Error('Failed to join waitlist');
      }
    } catch (error) {
      console.error('Waitlist signup error:', error);
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setIsLoading(false);
      setIsDetectingLocation(false);
    }
  };

  const handleBackToApp = () => {
    navigate('/');
  };

  const handleCalculatorClick = () => {
    // This would open the Creator Tool Cost Calculator
    window.open('/creator-tool-calculator', '_blank');
  };

  return (
    <div className={styles.container}>
      {/* Background Effects */}
      <div className={styles.backgroundEffects}>
        <div className={styles.gradientOrb1}></div>
        <div className={styles.gradientOrb2}></div>
        <div className={styles.gradientOrb3}></div>
      </div>

      {/* Header Badge - Updated for Competitive Positioning */}
      <div className={styles.headerBadge}>
        <FontAwesomeIcon icon={faBolt} />
        <span>For Professionals Tired of Managing 9+ Different AI Tools</span>
      </div>

      <div className={styles.mainContent}>
        {/* Left Content */}
        <div className={styles.leftContent}>
          {/* V16 Pain-First Hierarchy: Large Hero + Smaller Pain Points */}
          <h1 className={styles.mainHeading}>
            <span className={styles.toolsText}>Stop Spending Hours on One Video</span>
          </h1>
          
          {/* Pain Points - Rotating Animation */}
          <div style={{margin: "-36px 0 -8px 0"}}>
            <style>{`
              @keyframes fadeInOut1 {
                0%, 18% { opacity: 1; transform: translateY(0); }
                20%, 100% { opacity: 0; transform: translateY(-10px); }
              }
              @keyframes fadeInOut2 {
                0%, 18% { opacity: 0; transform: translateY(10px); }
                20%, 38% { opacity: 1; transform: translateY(0); }
                40%, 100% { opacity: 0; transform: translateY(-10px); }
              }
              @keyframes fadeInOut3 {
                0%, 38% { opacity: 0; transform: translateY(10px); }
                40%, 58% { opacity: 1; transform: translateY(0); }
                60%, 100% { opacity: 0; transform: translateY(-10px); }
              }
              @keyframes fadeInOut4 {
                0%, 58% { opacity: 0; transform: translateY(10px); }
                60%, 78% { opacity: 1; transform: translateY(0); }
                80%, 100% { opacity: 0; transform: translateY(-10px); }
              }
              @keyframes fadeInOut5 {
                0%, 78% { opacity: 0; transform: translateY(10px); }
                80%, 98% { opacity: 1; transform: translateY(0); }
                100% { opacity: 0; transform: translateY(-10px); }
              }
            `}</style>
            <h2 style={{
              fontSize: 'clamp(2rem, 3vw, 2.5rem)',
              fontWeight: '700',
              letterSpacing: '0.3px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '2em',
              whiteSpace: 'nowrap',
              margin: '0',
              lineHeight: '2.0'
            }}>
              <span style={{ color: '#ffffff' , textDecorationLine: "underline"}}>No More</span>
              <div style={{
                position: 'relative',
                height: '1.5em',
                minWidth: '500px',
                display: 'flex',
                alignItems: 'center',
                color: '#fbbf24'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: '800',
                  position: 'absolute',
                  animation: 'fadeInOut1 6s infinite',
                  width: '100%'
                }}>8-Hour Creation Marathons</div>
                <div style={{
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: '800',
                  position: 'absolute',
                  animation: 'fadeInOut2 6s infinite',
                  width: '100%'
                }}>Wasted Production Time</div>
              </div>
            </h2>
          </div>

          {/* Enhanced V16 Sub-heading */}
          <h2 className={styles.subHeading}>
            <span style={{
              background: 'linear-gradient(135deg, #10b981, #06d6a0)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: '800'
            }}>Create professional AI video content in 16 minutes.</span>{' '}
            <span style={{
              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: '800'
            }}>Idea to published without recording.</span>
          </h2>

          {/* Simplified Description */}
          <p className={styles.description}>
            <strong>⚡ Save hours every week for what matters most</strong>
            <br />
            <strong>🎯 FIRST 100 ONLY: $19.99/month (vs $200+ tool subscription chaos)</strong>
          </p>

          {/* Email Signup Form - Made More Prominent */}
          <form onSubmit={handleEmailSubmit} className={styles.emailForm}>
            <div className={styles.inputGroup}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email for first 100 exclusive access"
                className={styles.emailInput}
                required
              />
              <button 
                type="submit" 
                className={styles.joinButton}
                disabled={isLoading}
              >
                {isDetectingLocation ? 'Detecting location...' : isLoading ? 'Joining...' : 'Start Your 16-Minute Workflow'}
              </button>
            </div>
            
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
            
            <div className={styles.pricingBadge}>
              <FontAwesomeIcon icon={faTag} />
              <span>Waitlist Exclusive: Secure $19.99/month founder pricing before public launch!</span>
            </div>
          </form>
        </div>

        {/* Right Content - Enhanced with Competitive Demo */}
        <div className={styles.rightContent}>
          {/* Large Countdown Timer */}
          <div className={styles.countdownSection}>
            <h3 className={styles.countdownTitle}>
              <FontAwesomeIcon icon={faClock} />
              Simple Workflow Launch In:
            </h3>
            <div className={styles.countdown}>
              <div className={styles.timeUnit}>
                <span className={styles.timeNumber}>{timeLeft.days}</span>
                <span className={styles.timeLabel}>Days</span>
              </div>
              <div className={styles.timeUnit}>
                <span className={styles.timeNumber}>{timeLeft.hours}</span>
                <span className={styles.timeLabel}>Hours</span>
              </div>
              <div className={styles.timeUnit}>
                <span className={styles.timeNumber}>{timeLeft.minutes}</span>
                <span className={styles.timeLabel}>Minutes</span>
              </div>
              <div className={styles.timeUnit}>
                <span className={styles.timeNumber}>{timeLeft.seconds}</span>
                <span className={styles.timeLabel}>Seconds</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2 */}
      <div >
        <div>
            <h1 className={styles.mainHeading} style={
              {
                textAlign: "center", 
                margin: "6rem 0 -3rem 0",
              }
              }>
              <span className={styles.toolsText}>Revolutionary Workflow</span>
          </h1>
        </div>
        <div className={styles.mainContent}>
           {/* Enhanced Platform Demo with Competitive Comparison */}
          <div className={styles.leftContent}>
            {/* Detailed Description - Moved Lower */}
            <p className={styles.description}>
              <strong>Transform your content creation from hours-long process to 16-minute 9-step workflow.</strong>
              <br />
              Script → prompts → audio → video → captions → soundtrack → titles → thumbnails → publishing.
              <br />
              <br />
              <strong>⚡ Save hours every week:</strong> 16-minute workflow vs hours-long creation processes
              <br />
              <strong>💰 Save $200+ monthly:</strong> One platform vs multiple tool subscriptions
              <br />
              <strong>😌 Focus on what matters:</strong> Spend time on strategy, not content creation
              <br />
              <strong>🎯 FIRST 100 ONLY:</strong> $19.99/month before public launch at $39.99
            </p>

            {/* Enhanced Features List with Complete Workflow */}
            <div className={styles.featuresList}>
              <div className={styles.feature}>
                <FontAwesomeIcon icon={faCheckCircle} className={styles.checkIcon} />
                <span><strong>Save Hours Every Week:</strong> From idea to published video in 16 minutes</span>
              </div>
              <div className={styles.feature}>
                <FontAwesomeIcon icon={faCheckCircle} className={styles.checkIcon} />
                <span><strong>No Recording Required:</strong> AI creates professional videos from your ideas</span>
              </div>
              <div className={styles.feature}>
                <FontAwesomeIcon icon={faCheckCircle} className={styles.checkIcon} />
                <span><strong>Focus on Strategy:</strong> Spend time on important work, not content creation</span>
              </div>
              <div className={styles.feature}>
                <FontAwesomeIcon icon={faCheckCircle} className={styles.checkIcon} />
                <span><strong>Replace Multiple Tools:</strong> One platform vs $200+ monthly subscription chaos</span>
              </div>
            </div>
          
          </div>

          <div className={styles.rightContent}>
            <div className={styles.platformDemo}>
              <div className={styles.demoHeader}>Simple Workflow vs. Complex Tool Chaos</div>
              <div className={styles.demoSubtitle}>Perfect Workflow Replaces Feature Overwhelm</div>
              
              {/* Kingdom Studio Workflow */}
              <div className={styles.workflowSection}>
                <div className={styles.newWay}>
                  <h4>Kingdom Studio Complete 9-Step Workflow:</h4>
                  <div className={styles.toolsGrid}>
                    <div className={styles.toolIcon}>📝</div>
                    <div className={styles.toolIcon}>🎯</div>
                    <div className={styles.toolIcon}>🎵</div>
                    <div className={styles.toolIcon}>🎬</div>
                    <div className={styles.toolIcon}>📝</div>
                    <div className={styles.toolIcon}>🎵</div>
                    <div className={styles.toolIcon}>✍️</div>
                    <div className={styles.toolIcon}>🖼️</div>
                    <div className={styles.toolIcon}>📤</div>
                  </div>
                  <div className={styles.newTotal}>Result: Idea-to-Published in One Integrated Platform</div>
                </div>
              </div>
              
              <div className={styles.workflowDemo}>
                <div className={styles.demoStep}>
                  <div className={styles.stepIcon}>📝</div>
                  <div className={styles.stepContent}>
                    <strong>1. Script → 2. Prompts → 3. Audio:</strong>
                    <p>Start with your idea → AI generates complete script → converts to video prompts → creates professional voiceover</p>
                  </div>
                </div>

                <div className={styles.workflowArrow}>↓</div>

                <div className={styles.demoStep}>
                  <div className={styles.stepIcon}>🎬</div>
                  <div className={styles.stepContent}>
                    <strong>4. Video → 5. Captions → 6. Audio:</strong>
                    <p>AI generates video from prompts → adds captions and text overlays → enhances with soundtrack and effects</p>
                  </div>
                </div>

                <div className={styles.workflowArrow}>↓</div>

                <div className={styles.demoStep}>
                  <div className={styles.stepIcon}>📤</div>
                  <div className={styles.stepContent}>
                    <strong>7. Titles → 8. Thumbnails → 9. Publish:</strong>
                    <p>Generates SEO titles and descriptions → creates eye-catching thumbnails → publishes to all platforms simultaneously</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
       
       
      
      </div>

      {/* Full-Width App-Switching Hell Section */}
      <div className={styles.fullWidthSection}>
        <div className={styles.urgencyBox}>
          <h4>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Save 15+ Hours Weekly - End the Multi-Hour Creation Marathon
          </h4>
          
          <div className={styles.toolStackComparison}>
            <div className={styles.currentStack}>
              <p><strong>Current Multi-App Nightmare = Hours of Creation Hell:</strong></p>
              <div className={styles.toolCosts}>
                <span>📝 ChatGPT for scripts → Copy/paste to next app (15 minutes wasted)</span>
                <span>🎵 ElevenLabs for voice → Download, upload to video tool (20 minutes wasted)</span>
                <span>🎬 Fliki/InVideo for video → Export, import to editor (30 minutes wasted)</span>
                <span>📱 Canva for thumbnails → Save, upload separately (25 minutes wasted)</span>
                <span>💬 Descript for captions → Generate, sync manually (45 minutes wasted)</span>
                <span>📤 Buffer/Hootsuite for publishing → Upload everything again (30 minutes wasted)</span>
                <span>🔄 <strong>= Hours of App-Switching Hell + $200+ Monthly Chaos 😤</strong></span>
              </div>
            </div>
            
            <div className={styles.solution}>
              <p><strong>Kingdom Studio: 16-Minute Professional Creation</strong></p>
              <div className={styles.kingdomValue}>
                <span>✅ AI script generation → <strong>Save 3+ hours weekly</strong></span>
                <span>✅ Professional voiceover → <strong>Save 2+ hours weekly</strong></span>
                <span>✅ Stunning video creation → <strong>Save 4+ hours weekly</strong></span>
                <span>✅ Auto captions & thumbnails → <strong>Save 2+ hours weekly</strong></span>
                <span>✅ Direct multi-platform publishing → <strong>Save 1+ hour weekly</strong></span>
                <span>✅ Zero file management → <strong>Save 3+ hours weekly</strong></span>
                <span><strong>= Complete Production Studio in One Place ✨</strong></span>
                <span><strong>BENEFIT: Script to Published, Zero App Switching</strong></span>
              </div>
            </div>
          </div>
          
          {/* <div className={styles.socialProof}>
            <div className={styles.proofItem}>
              <span className={styles.proofNumber}>{subscriberCount}+</span>
              <span>Creators saving 15+ hours weekly</span>
            </div>
            <div className={styles.proofItem}>
              <span className={styles.proofNumber}>$850,000+</span>
              <span>Saved monthly (vs tool chaos)</span>
            </div>
            <div className={styles.proofItem}>
              <span className={styles.proofNumber}>Zero Stress</span>
              <span>Professional results</span>
            </div>
          </div> */}
          
          {/* Email Signup Form */}
          <form onSubmit={handleEmailSubmit} className={styles.emailForm}>
            <div className={styles.inputGroup}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email for first 100 exclusive access"
                className={styles.emailInput}
                required
              />
              <button 
                type="submit" 
                className={styles.joinButton}
                disabled={isLoading}
              >
                {isDetectingLocation ? 'Detecting location...' : isLoading ? 'Joining...' : 'Start Your 16-Minute Workflow'}
              </button>
            </div>
            
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
            
            <div className={styles.pricingBadge}>
              <FontAwesomeIcon icon={faTag} />
              <span>Waitlist Exclusive: Secure $19.99/month founder pricing before public launch!</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SoftLaunch;